/**
 * Application entry point.
 *
 * The whole site is one document. A route is a language and, optionally, a
 * policy page; changing either re-renders, rewrites the URL and updates the
 * document head, without a network round trip.
 */

import './styles/tokens.css';
import './styles/app.css';

import { CONTENT, DEFAULT_LANG, LANGS } from './content.js';
import { UNCONFIRMED } from './company.js';
import { shell } from './components/chrome.js';
import { renderPage } from './components/page.js';
import { renderLegal } from './components/legal.js';
import { renderPay } from './components/pay.js';
import { LEGAL, PAGES } from './legal.js';
import { h, mount } from './lib/dom.js';
import {
  ROUTES, initialLang, interceptLinks, langFromPath, pageFromPath, onRouteChange, pathFor, pushRoute, rememberLang,
} from './lib/router.js';

const root = document.getElementById('app');
let current = null;
let currentPage = null;

function render(lang, page = null, { push = false } = {}) {
  if (!LANGS.includes(lang)) lang = DEFAULT_LANG;
  if (!ROUTES.includes(page)) page = null;
  if (lang === current && page === currentPage) return;

  const samePage = page === currentPage;
  const y = window.scrollY;
  current = lang;
  currentPage = page;

  mount(root, shell({ lang, page }, view(lang, page), { onLangChange: switchTo }), devBanner());
  updateHead(lang, page);
  if (push) pushRoute(lang, page);

  // A language switch is not a navigation — hold the reader where they were.
  // Moving between pages is one, so start that at the top.
  window.scrollTo(0, samePage ? y : 0);
}

// Switching language keeps you on the page you were reading.
/** Every page shares the shell; only what sits between nav and footer differs. */
function view(lang, page) {
  if (PAGES.includes(page)) return renderLegal(lang, page);
  if (page === 'pay') return renderPay(lang);
  return renderPage(lang);
}

function switchTo(lang) {
  rememberLang(lang);
  render(lang, currentPage, { push: true });
}

// Following an internal link, without the reload.
function go(lang, page) {
  render(lang, page, { push: true });
}

/**
 * Keep the head in step with the rendered language: crawlers, share cards and
 * screen readers all read it, and none of them see the DOM we just built.
 */
function updateHead(lang, page = null) {
  const c = CONTENT[lang];
  const url = new URL(pathFor(lang, page), location.origin).href;
  const title = !page
    ? c.meta.title
    : `${page === 'pay' ? c.pay.title : LEGAL[lang][page].title} — ${c.footer.company}`;

  document.documentElement.lang = c.htmlLang;
  document.title = title;

  setMeta('name', 'description', c.meta.description);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', c.meta.description);
  setMeta('property', 'og:locale', c.htmlLang.replace('-', '_'));
  setMeta('property', 'og:url', url);

  setLink('canonical', url);
  for (const l of LANGS) setLink('alternate', new URL(pathFor(l, page), location.origin).href, l);
  setLink('alternate', new URL(pathFor(DEFAULT_LANG, page), location.origin).href, 'x-default');
}

function setMeta(attr, key, value) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.append(tag);
  }
  tag.setAttribute('content', value);
}

function setLink(rel, href, hreflang = null) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = rel;
    if (hreflang) tag.hreflang = hreflang;
    document.head.append(tag);
  }
  tag.href = href;
}

/**
 * In development only: the business facts nobody has confirmed yet. A
 * production build refuses to run while any remain (see vite.config.js).
 */
function devBanner() {
  if (!import.meta.env.DEV || !UNCONFIRMED.length) return null;
  return h(
    'aside',
    { class: 'dev-banner', role: 'note' },
    h('strong', null, 'Not deployable yet — unconfirmed: '),
    UNCONFIRMED.map((u) => u.what).join('; '),
  );
}

render(initialLang(), pageFromPath());
onRouteChange((lang, page) => render(lang, page));
interceptLinks(root, go);

// A first visit to "/" that the browser or a past visit says should be read in
// another language: move the URL to match, replacing rather than pushing so the
// back button still leaves the site.
if (!langFromPath() && current !== DEFAULT_LANG) {
  history.replaceState({ lang: current, page: currentPage }, '', pathFor(current, currentPage) + location.hash);
}

// The hash is resolved before the page exists, so honour it once it does.
if (location.hash) {
  document.getElementById(location.hash.slice(1))?.scrollIntoView();
}
